'use strict';

//==================================================================
// Utility functions dealing course relate
//===================================================================

import React from 'react';
import {isDigits} from "./ValidationUtils";
import {capitalize} from './Utils';

/**
 * Given a course code, returns a course code in a consistent and predictable state.
 */
export const normalize = (courseCode) => {
    if (!courseCode) {
        return courseCode;
    }
    // If KRAD has replaced '&' with '%26' or contains the html '&amp;', replace it back before normalizing
    courseCode = courseCode.replace(/%26|&amp;/gi,'&');

    // Uppercase it.
    courseCode = courseCode.toUpperCase();
    // If there aren't any spaces in the course code then add one.
    let regxp = /([^0-9]*)(\d*)$/g;
    let match = regxp.exec(courseCode);
    let cc = match[1] + " " + match[2];
    // Collapse the whitespace.
    cc = cc.replace(/\s+/g, ' ');
    return cc;
};

/**
 * Given a string of numbers to be read as individual numbers
 * (e.g. 123 would be "one two three" and NOT "one hundred twenty three")
 * Only to be used in a11y labels or screen reader text.
 * @param number - code, SLN, or any string of numbers
 * @returns {string} of individually spaced numbers
 */
export const a11yCode = (number) => {
    if (!number || !isDigits(number)) {
        return number;
    }
    return number?.split('').join(' ');
}

/**
 * Given a course code checks if it is a valid course placeholder format eg. CHEM 2xx
 * This regex will match a placeholder of the format - subject area and level with trailing xx
 * Subject area is a combination of letter, space or &. Has to always start with a letter.
 * code and the number.
 * @param courseCode
 * @returns {(RegExpExecArray | {groups: {}}) | boolean}
 */
export const isCoursePlaceholder = (courseCode) => {
    let regxp =/^[a-zA-Z]{1}[a-zA-Z &]{2,7}[0-9]XX$/ig
    let match = regxp.exec(courseCode);
    if (match){
        return match.length === 1
    } else {
        return false;
    }

};

/**
 * Given a coded string of the days of the week, returns an element with aria-label
 * to give the full names of the days of the week for accessibility.
 * @param daysOfWeek - coded string of days of the week.
 * M = Monday, T = Tuesday, W = Wednesday, Th = Thursday, F = Friday, S = Saturday
 * @return span element with aria-label with coded string as child
 */
export const daysOfWeekAbbreviation = (daysOfWeek) => {
    if (daysOfWeek) {
        // remove all whitespace and split the string by uppercase characters
        let dayCodes = daysOfWeek.replace(/\s+/g, '').split(/(?=[A-Z])/);
        let listOfDays = dayCodes.map(day => {
            switch (day) {
                case "M":
                    return "Monday";
                case "T":
                    return "Tuesday";
                case "W":
                    return "Wednesday";
                case "Th":
                    return "Thursday";
                case "F":
                    return "Friday";
                case "S":
                    return "Saturday";
                default:
                    return null;
            }
        }).filter(day => day);

        if (listOfDays.length === 0) {
            return daysOfWeek;
        } else {
            return (
                <>
                    <span className="sr-only">{listOfDays.join(", ")}</span>
                    <span title={listOfDays.join(", ")} aria-hidden={true}>{daysOfWeek.replace(/\s+/g, '')}</span>
                </>
            );
        }
    } else {
        return daysOfWeek;
    }
};
